"use client"
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { getAuthHeader, getToken } from "../utils/auth";
import { useOutletContext, useNavigate } from "react-router-dom";
import { FileSpreadsheet } from "lucide-react";
import { SALESBREAKER_ENGLISH_GUIDE_URL, getEnglishPlanName, isEnglishOperatorMode } from "../utils/operatorMode";

// --- 定数 ---
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;
const REPORT_TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/templates/send-result-simple-report-template.xlsx`;

// --- 以前のカードデザインを継承 ---
const cardStyle = "bg-white p-6 rounded-lg shadow-md";
const cardTitleStyle = "text-lg font-semibold text-gray-700 mb-4";
const DEFAULT_PLAN_INFO = { plan_type: "none", plan_name: "確認中..." };
const DEFAULT_META_STATS = { totalLeads: 0, conversions: 0, revenue: 0 };
const DEFAULT_USAGE_LIMITS = {
    success: false,
    limits: {
        email: { current: 0, limit: null }
    }
};

async function fetchJsonSafely(url, options, fallback, label) {
    try {
        const response = await fetch(url, options);
        const raw = await response.text();

        if (!raw) {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`.trim());
            }
            return fallback;
        }

        let data;
        try {
            data = JSON.parse(raw);
        } catch (parseError) {
            throw new Error(`${label} returned a non-JSON response`);
        }

        if (!response.ok) {
            throw new Error(data?.error || `${response.status} ${response.statusText}`.trim());
        }

        return data;
    } catch (error) {
        console.warn(`[MyPage] ${label} fetch failed:`, error);
        return fallback;
    }
}

// --- 1. ライブログ表示コンポーネント (日本語化) ---
const LiveLogDisplay = React.memo(({ logs, isEnglishBeta = false }) => {
    const logContainerRef = useRef(null);
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);

    const formatLogMessage = (log) => {
        const timeMatch = log.match(/^\[(\d{2}:\d{2}:\d{2})\]/);
        if (!timeMatch) return log;
        const timeString = timeMatch[1];
        const message = log.substring(timeMatch[0].length).trim();
        return `[${timeString} ${isEnglishBeta ? "JST" : "日本標準時"}] ${message}`;
    };

    useEffect(() => {
        if (isAutoScrollEnabled && logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [logs, isAutoScrollEnabled]);

    const handleScroll = () => {
        if (!logContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 20;
        setIsAutoScrollEnabled(isAtBottom);
    };

    return (
        <div
            ref={logContainerRef}
            onScroll={handleScroll}
            className="bg-gray-900 text-white font-mono text-xs p-4 rounded-md h-40 overflow-y-auto mt-4 border border-gray-700"
        >
            {logs.length > 0 ? (
                logs.map((log, index) => (
                    <p key={index} className="whitespace-pre-wrap leading-relaxed">
                        <span className="text-gray-500 mr-2">{'>'}</span>{formatLogMessage(log)}
                    </p>
                ))
            ) : (
                <p className="text-gray-500">{isEnglishBeta ? "Waiting for execution logs..." : "サーバーからの報告を待機中..."}</p>
            )}
        </div>
    );
});
LiveLogDisplay.displayName = 'LiveLogDisplay';

// --- 2. ジョブカードコンポーネント ---
const JobCard = React.memo(({ job, onCancel, logs, isEnglishBeta = false }) => {
    if (!job) return null;
    const progressRate = job.total > 0 ? Math.min((job.progress / job.total) * 100, 100) : 0;
    const statusText = job.status === 'pending'
        ? (isEnglishBeta ? 'Queued' : '待機中')
        : (isEnglishBeta ? 'Processing' : '処理中');
    const statusColor = job.status === 'pending' ? 'bg-yellow-500' : 'bg-blue-600';

    return (
        <div className="bg-gray-50 p-4 rounded-md border transition-all duration-300">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-2">
                <p className="text-sm text-gray-500">{isEnglishBeta ? "Execution ID" : "配信ID"}: {job.id} | {isEnglishBeta ? "Status" : "ステータス"}: <span className="font-medium">{statusText}</span></p>
                <button onClick={() => onCancel(job.id)} className="mt-2 sm:mt-0 px-3 py-1 text-xs font-semibold text-white bg-red-600 rounded-md hover:bg-red-700">{isEnglishBeta ? "Cancel" : "キャンセル"}</button>
            </div>
            <div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div className={`${statusColor} h-4 rounded-full transition-all duration-500 ease-out`} style={{ width: `${progressRate}%` }} />
                </div>
                <p className="text-right text-sm font-medium text-gray-700 mt-2">{job.progress} / {job.total} {isEnglishBeta ? "targets" : "件"}</p>
            </div>
            <LiveLogDisplay logs={logs} isEnglishBeta={isEnglishBeta} />
        </div>
    );
});
JobCard.displayName = 'JobCard';

// --- 3. 実行中ジョブモニター ---
function ActiveJobMonitor({ isEnglishBeta = false }) {
    const [activeJobs, setActiveJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [jobLogs, setJobLogs] = useState({});
    const { user } = useOutletContext();

    const selectedClientId = useMemo(() => {
        if (!user) return "";
        if (user.role === "admin" || user.role === "admin_staff") {
             return localStorage.getItem("selectedClientId") || user.client_id || "";
        }
        return user.client_id || "";
    }, [user]);

    useEffect(() => {
        if (!selectedClientId) return;
        const token = getToken();
        if (!token) return;
        const eventSourceUrl = `${API_URL}/api/log-stream?clientId=${selectedClientId}&token=${token}`;
        const eventSource = new EventSource(eventSourceUrl);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.jobId && data.message) {
                    setJobLogs(prev => ({ ...prev, [data.jobId]: [...(prev[data.jobId] || []), data.message].slice(-8) }));
                }
            } catch (e) { console.log("ログパース失敗"); }
        };
        return () => eventSource.close();
    }, [selectedClientId]);

    const fetchJobStatus = useCallback(async () => {
        if (!selectedClientId) { setIsLoading(false); return; }
        try {
            const authHeader = getAuthHeader();
            const res = await fetch(`${API_URL}/api/jobs/active?client_id=${selectedClientId}`, { headers: authHeader });
            const data = await res.json();
            if (data.success) {
                setActiveJobs(data.jobs.filter(job => job.job_type === 'send_campaign'));
                setError(null);
            }
        } catch (err) { setError(err.message); }
        finally { setIsLoading(false); }
    }, [selectedClientId]);

    useEffect(() => {
        fetchJobStatus();
        const interval = setInterval(fetchJobStatus, 5000);
        return () => clearInterval(interval);
    }, [fetchJobStatus]);

    const handleCancelJob = async (jobId) => {
        if (!window.confirm(isEnglishBeta ? "Cancel this execution job?" : "送信をキャンセルしますか？")) return;
        try {
            const authHeader = getAuthHeader();
            const res = await fetch(`${API_URL}/api/jobs/cancel`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeader }, body: JSON.stringify({ jobId }) });
            if ((await res.json()).success) {
                setActiveJobs(prev => prev.filter(job => job.id !== jobId));
            }
        } catch (err) { console.error(err); }
    };

    if (isLoading) return <div className={cardStyle}><h3 className={cardTitleStyle}>{isEnglishBeta ? "Execution Monitor" : "実行状況"}</h3><p>{isEnglishBeta ? "Checking..." : "確認中..."}</p></div>;
    if (activeJobs.length === 0 && !error) return null;

    return (
        <div className={`${cardStyle} border-l-4 border-blue-500`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{isEnglishBeta ? `${activeJobs.length} active execution job${activeJobs.length === 1 ? "" : "s"}` : `${activeJobs.length}件実行中`}</h3>
             {error && <p className="text-red-600 mb-4">{isEnglishBeta ? "Fetch error" : "取得エラー"}: {error}</p>}
            <div className="space-y-4">
                {activeJobs.map((job) => (<JobCard key={job.id} job={job} onCancel={handleCancelJob} logs={jobLogs[job.id] || []} isEnglishBeta={isEnglishBeta} />))}
            </div>
        </div>
    );
}

// --- 6. 各種モーダルコンポーネント ---
function AnnouncementModal({ isOpen, onClose, announcement }) {
    if (!isOpen || !announcement) return null;
    return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"> <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"> <div className="flex justify-between items-center p-4 border-b"> <h2 className="text-xl font-bold text-gray-800">{announcement.title}</h2> <button onClick={onClose}>✕</button> </div> <div className="p-6 overflow-y-auto flex-grow"><p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p></div> <div className="p-4 bg-gray-50 border-t flex justify-end"><button onClick={onClose} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md">閉じる</button></div> </div> </div> )
}

function HotLeadPopup({ isOpen, onClose, leadCount, type, onNavigate }) {
    if (!isOpen || leadCount === 0) return null;
    const isMeta = type === 'inbound';
    return ( <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[999] p-4"> <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 text-center"> <div className="text-6xl mb-4">🔥</div> <h2 className="text-2xl font-bold mb-2">{isMeta ? '未案件化リード通知' : 'ホットリード通知'}</h2> <p className="text-lg mb-4"> <span className="text-3xl font-bold text-red-600">{leadCount}</span> 件の{isMeta ? '未対応リード' : '未対応ホットリード'}があります！ </p> <div className="flex flex-col sm:flex-row gap-3 justify-center"> <button onClick={onNavigate} className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg">🚀 リードを確認する</button> <button onClick={onClose} className="bg-gray-200 py-3 px-6 rounded-md">閉じる</button> </div> </div> </div> );
}

function HotLeadCard({ hotLeads, onNavigateToDeals, isEnglishBeta = false }) {
    return (
        <div className={cardStyle}>
            <h3 className={cardTitleStyle}>
                {isEnglishBeta ? "Engaged companies" : "🔥 ホットリード"} <span className="text-sm font-normal text-gray-500">{isEnglishBeta ? "(2+ engagement events)" : "(未対応・クリック2回以上)"}</span>
            </h3>
            {hotLeads.length > 0 ? (
                <ul className="space-y-3">
                    {hotLeads.slice(0, 5).map((lead) => (
                        <li key={lead.deal_id || `${lead.sales_list_id}-${lead.latest_click_at}`} className="flex justify-between items-center p-3 bg-orange-50 rounded-md border border-orange-200">
                            <div><p className="font-semibold text-gray-800">{lead.company}</p><p className="text-xs text-gray-500">{isEnglishBeta ? "Engagement events" : "クリック回数"}: {lead.click_count}{isEnglishBeta ? "" : "回"}</p></div>
                            <button onClick={onNavigateToDeals} className="px-3 py-1 text-xs font-semibold text-white bg-blue-600 rounded-md">{isEnglishBeta ? "Open Events" : "案件管理で確認"}</button>
                        </li>
                    ))}
                </ul>
            ) : (<p className="text-sm text-gray-500">{isEnglishBeta ? "No high-engagement companies yet." : "現在、未対応のホットリードはありません。"}</p>)}
        </div>
    );
}

function ClientSelectorBar({ isAdmin, clientFilter, setClientFilter }) {
    if (!isAdmin) return null;
    return (
        <div className={cardStyle}>
            <div className="max-w-xl">
                <label className="text-sm font-medium text-gray-600 block mb-2">クライアント選択</label>
                <input
                    type="text"
                    value={clientFilter}
                    onChange={(e) => {
                        setClientFilter(e.target.value);
                        localStorage.setItem("selectedClientId", e.target.value);
                    }}
                    placeholder="client_id を入力"
                    className="w-full p-3 border rounded-md"
                />
            </div>
        </div>
    );
}

function formatDateTimeLabel(value, isEnglishBeta = false) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const formatted = date.toLocaleString(isEnglishBeta ? "en-US" : "ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Tokyo",
    });
    return isEnglishBeta ? `${formatted} JST` : formatted;
}

function getCtrComment(ctr, isEnglishBeta = false) {
    if (ctr === null) return isEnglishBeta ? "No submitted target base yet." : "有効送信数がまだありません";
    if (ctr < 0.3) return isEnglishBeta ? "Low range: review Japanese copy and target fit." : "低め: 文面やリスト精度の見直し余地あり";
    if (ctr <= 1) return isEnglishBeta ? "Standard range: typical early engagement." : "平均レンジ: 標準的な反応です";
    return isEnglishBeta ? "High range: copy and target fit are performing well." : "高め: 文面やターゲットが刺さっています";
}

const MAIL_FALLBACK_TARGET_STATUSES = "NO_FORM_FOUND,DEAD_URL";

const MAIL_FALLBACK_STATUS_LABELS = {
    NO_FORM_FOUND: "フォーム未検出",
    DEAD_URL: "URL無効",
    queued: "送信待ち",
    sending: "送信中",
    sent: "送信済み",
    failed: "送信失敗",
    skipped: "対象外スキップ",
    bounce: "バウンス",
};

function humanizeMailFallbackStatus(value) {
    if (!value) return "-";
    return MAIL_FALLBACK_STATUS_LABELS[value] || value;
}

function MailFallbackJobPanel({ jobId, clientId, verifiedSenderProfile }) {
    const [isPreparing, setIsPreparing] = useState(false);
    const [isPreviewingSend, setIsPreviewingSend] = useState(false);
    const [isQueueing, setIsQueueing] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [hasAutoPrepared, setHasAutoPrepared] = useState(false);
    const [prepareResult, setPrepareResult] = useState(null);
    const [sendPreview, setSendPreview] = useState(null);
    const [queueApplyResult, setQueueApplyResult] = useState(null);
    const [sendApplyResult, setSendApplyResult] = useState(null);
    const [error, setError] = useState("");

    const summaryRows = prepareResult?.summary?.summary || [];
    const fallbackTotal = summaryRows.reduce((sum, row) => sum + Number(row.total_count || 0), 0);
    const summaryTotals = prepareResult?.summary?.totals || null;
    const fallbackSamples = prepareResult?.summary?.samples || [];
    const canUseMailFallback = Boolean(verifiedSenderProfile?.sender_email);
    const disabledReason = canUseMailFallback
        ? ""
        : "DNS / SES の送信元認証が完了した送信元プロフィールがまだないため、公開メールへの補足送信はまだ実行できません。";
    const hasQueuedBatch = Boolean(queueApplyResult?.inserted > 0 || queueApplyResult?.alreadyQueued);

    const handlePrepare = useCallback(async ({ silent = false, preserveSendResult = false } = {}) => {
        if (!clientId || !canUseMailFallback) return;
        setIsPreparing(true);
        if (!silent) {
            setError("");
        }
        try {
            const response = await fetch(`${API_URL}/api/mail-fallback/jobs/${jobId}/prepare`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({
                    client_id: clientId,
                    statuses: MAIL_FALLBACK_TARGET_STATUSES,
                    apply: true,
                    inventory_limit: 5000,
                    summary_limit: 20,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data?.success) {
                throw new Error(data?.error || "公開メールへの補足送信候補の準備に失敗しました。");
            }
            setPrepareResult(data);
            setSendPreview(null);
            setQueueApplyResult(null);
            if (!preserveSendResult) {
                setSendApplyResult(null);
            }
            return data;
        } catch (fetchError) {
            console.error("[MyPage] mail fallback prepare failed:", fetchError);
            if (!silent) {
                setError(fetchError.message || "公開メールへの補足送信候補の準備に失敗しました。");
            }
            throw fetchError;
        } finally {
            setIsPreparing(false);
        }
    }, [canUseMailFallback, clientId, jobId]);

    const handleQueueApply = useCallback(async (showConfirm = true) => {
        if (!clientId || !canUseMailFallback) return;
        if (showConfirm && !window.confirm("このJOBに紐づく公開メールへの補足送信候補を送信キューへ追加します。1JOBにつき1回だけ送る前提です。続けますか？")) {
            return;
        }
        setIsQueueing(true);
        setError("");
        try {
            const response = await fetch(`${API_URL}/api/mail-fallback/jobs/${jobId}/queue`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({
                    client_id: clientId,
                    statuses: MAIL_FALLBACK_TARGET_STATUSES,
                    apply: true,
                    limit: 1000,
                    template_variant: "public_email_fallback",
                }),
            });
            const data = await response.json();
            if (!response.ok || !data?.success) {
                throw new Error(data?.error || "補足送信キューの作成に失敗しました。");
            }
            setQueueApplyResult(data);
            setSendApplyResult(null);
            return data;
        } catch (fetchError) {
            console.error("[MyPage] mail fallback queue apply failed:", fetchError);
            setError(fetchError.message || "補足送信キューの作成に失敗しました。");
            throw fetchError;
        } finally {
            setIsQueueing(false);
        }
    }, [canUseMailFallback, clientId, jobId]);

    const handlePreviewSend = useCallback(async ({ silent = false } = {}) => {
        if (!clientId || !canUseMailFallback) return;
        setIsPreviewingSend(true);
        if (!silent) {
            setError("");
        }
        try {
            const response = await fetch(`${API_URL}/api/mail-fallback/send`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({
                    client_id: clientId,
                    job_id: Number(jobId),
                    template_variant: "public_email_fallback",
                    limit: 3,
                    apply: false,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data?.success) {
                throw new Error(data?.error || "補足送信メールのプレビュー取得に失敗しました。");
            }
            setSendPreview(data);
            return data;
        } catch (fetchError) {
            console.error("[MyPage] mail fallback send preview failed:", fetchError);
            if (!silent) {
                setError(fetchError.message || "補足送信メールのプレビュー取得に失敗しました。");
            }
            throw fetchError;
        } finally {
            setIsPreviewingSend(false);
        }
    }, [canUseMailFallback, clientId, jobId]);

    const handleSendApply = useCallback(async () => {
        setIsSending(true);
        setError("");
        try {
            let preparedData = prepareResult;
            if (!preparedData) {
                preparedData = await handlePrepare({ silent: true });
            }
            const preparedRows = preparedData?.summary?.summary || [];
            const preparedTotal = preparedRows.reduce((sum, row) => sum + Number(row.total_count || 0), 0);
            if (preparedTotal <= 0) {
                throw new Error("このJOBでは、メールで送れる公開メールアドレスはまだ見つかっていません。");
            }

            let previewData = sendPreview;
            if (!previewData) {
                previewData = await handlePreviewSend({ silent: true });
            }
            const previewSample = previewData?.samples?.[0] || null;
            const bodyPreview = previewSample?.body
                ? previewSample.body.split("\n").slice(0, 5).join("\n")
                : "本文は送信時にテンプレートから生成されます。";
            const confirmMessage = [
                `フォーム送信で送れなかった企業のうち、公開メールアドレスが見つかった ${preparedTotal.toLocaleString()} 件にメールを送信します。`,
                "",
                "この送信は特定電子メール法に沿った文面と配信停止導線つきで、1JOBにつき1回だけ実行できます。",
                verifiedSenderProfile?.sender_email ? `送信元: ${verifiedSenderProfile.sender_email}` : "",
                previewSample?.subject ? `件名: ${previewSample.subject}` : "",
                "",
                "本文冒頭イメージ:",
                bodyPreview,
                "",
                "送信しますか？",
            ].filter(Boolean).join("\n");
            if (!window.confirm(confirmMessage)) {
                return;
            }

            if (!hasQueuedBatch) {
                await handleQueueApply(false);
            }
            const response = await fetch(`${API_URL}/api/mail-fallback/send`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({
                    client_id: clientId,
                    job_id: Number(jobId),
                    template_variant: "public_email_fallback",
                    apply: true,
                    limit: 1000,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data?.success) {
                throw new Error(data?.error || "補足送信メールの送信に失敗しました。");
            }
            setSendApplyResult(data);
            await handlePrepare({ silent: true, preserveSendResult: true }).catch(() => {});
        } catch (fetchError) {
            console.error("[MyPage] mail fallback send apply failed:", fetchError);
            setError(fetchError.message || "補足送信メールの送信に失敗しました。");
        } finally {
            setIsSending(false);
        }
    }, [canUseMailFallback, clientId, handlePrepare, handlePreviewSend, handleQueueApply, hasQueuedBatch, jobId, prepareResult, sendPreview, verifiedSenderProfile?.sender_email]);

    useEffect(() => {
        if (!clientId || !canUseMailFallback || hasAutoPrepared || isDismissed) return;
        setHasAutoPrepared(true);
        handlePrepare({ silent: true }).catch(() => {});
    }, [canUseMailFallback, clientId, handlePrepare, hasAutoPrepared, isDismissed]);

    if (isDismissed && !sendApplyResult) {
        return null;
    }

    return (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                    <p className="text-sm font-semibold text-slate-800">メールで送れる企業</p>
                    <p className="mt-1 text-xs text-slate-500">
                        {prepareResult
                            ? fallbackTotal > 0
                                ? `フォーム送信で送信できなかった企業のうち、公開メールアドレスが見つかった企業が ${fallbackTotal.toLocaleString()} 件あります。`
                                : "このJOBでは、公開メールアドレスが見つかった企業はまだありません。"
                            : "フォーム送信で送信できなかった企業の中から、公開メールアドレスが見つかった企業を確認しています。"}
                    </p>
                    <p className="mt-1 text-[11px] text-amber-700">
                        DNS / SES 設定が完了しているクライアントだけ送信できます。配信停止導線つきの文面で送信します。
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                        対象はこのJOBでフォーム送信できなかった企業のうち、公開メールアドレスが確認できた企業のみです。送信実行は1JOBにつき1回だけです。
                    </p>
                    {canUseMailFallback ? (
                        <p className="mt-1 text-[11px] text-emerald-700">
                            送信元: {verifiedSenderProfile.sender_email}（SES 認証済み）
                        </p>
                    ) : (
                        <p className="mt-1 text-[11px] text-red-600">
                            {disabledReason}
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleSendApply}
                        disabled={isSending || isQueueing || isPreparing || isPreviewingSend || !canUseMailFallback}
                        className="rounded-md border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isPreparing ? "候補確認中..." : isPreviewingSend ? "本文確認中..." : isSending ? "送信中..." : "メール送信する"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsDismissed(true)}
                        disabled={isSending || isQueueing}
                        className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        今回は送信しない
                    </button>
                </div>
            </div>

            {error && <p className="mt-3 text-xs font-medium text-red-600">{error}</p>}

            {prepareResult && (
                <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-md border bg-white p-3">
                            <p className="text-[11px] text-slate-500 mb-1">対象候補</p>
                            <p className="text-lg font-bold text-slate-800">{Number(prepareResult.materialize?.rows || 0).toLocaleString()}</p>
                        </div>
                        <div className="rounded-md border bg-white p-3">
                            <p className="text-[11px] text-slate-500 mb-1">公開メール確認数</p>
                            <p className="text-lg font-bold text-slate-800">{Number(prepareResult.materialize?.extractedEmails || 0).toLocaleString()}</p>
                        </div>
                        <div className="rounded-md border bg-white p-3">
                            <p className="text-[11px] text-slate-500 mb-1">今回送れる件数</p>
                            <p className="text-lg font-bold text-blue-700">{fallbackTotal.toLocaleString()}</p>
                        </div>
                    </div>

                    {summaryTotals && (
                        <div className="rounded-md border bg-white p-3">
                            <p className="text-[11px] text-slate-500 mb-2">現在の状況</p>
                            <div className="flex flex-wrap gap-2">
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                                    送信待ち {Number(summaryTotals.queued || 0).toLocaleString()}
                                </span>
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                                    送信中 {Number(summaryTotals.sending || 0).toLocaleString()}
                                </span>
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                    送信済み {Number(summaryTotals.sent || 0).toLocaleString()}
                                </span>
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-rose-700">
                                    送信失敗 {Number(summaryTotals.failed || 0).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    )}

                    {fallbackSamples.length > 0 && (
                        <div className="rounded-md border bg-white p-3">
                            <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                                <p className="text-[11px] text-slate-500">送信先一覧</p>
                                <p className="text-[11px] text-slate-400">
                                    {fallbackTotal > fallbackSamples.length
                                        ? `直近 ${fallbackSamples.length.toLocaleString()} 件を表示`
                                        : `${fallbackSamples.length.toLocaleString()} 件を表示`}
                                </p>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {fallbackSamples.map((sample) => {
                                    const status = sample.latest_event_status || "";
                                    const statusLabel = status ? humanizeMailFallbackStatus(status) : "未送信";
                                    const statusClass = status === "sent"
                                        ? "text-emerald-700"
                                        : status === "failed"
                                            ? "text-rose-700"
                                            : "text-slate-600";
                                    return (
                                        <div key={`${sample.id}-${sample.public_email}`} className="grid gap-1 py-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:items-center">
                                            <div className="min-w-0">
                                                <p className="truncate text-xs font-semibold text-slate-800" title={sample.company_name || sample.target_domain || ""}>
                                                    {sample.company_name || sample.target_domain || "会社名未取得"}
                                                </p>
                                                {sample.target_domain && (
                                                    <p className="truncate text-[11px] text-slate-400" title={sample.target_domain}>
                                                        {sample.target_domain}
                                                    </p>
                                                )}
                                            </div>
                                            <p className="min-w-0 truncate text-xs text-slate-600" title={sample.public_email || ""}>
                                                {sample.public_email || "メール未取得"}
                                            </p>
                                            <p className={`text-xs font-semibold ${statusClass}`}>{statusLabel}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {sendApplyResult && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
                            <p className="text-[11px] text-rose-700 mb-1">送信結果</p>
                            <p className="text-sm font-semibold text-rose-800">
                                送信対象 {Number(sendApplyResult.selected || 0).toLocaleString()} 件 / 送信済み {Number(sendApplyResult.sent || 0).toLocaleString()} 件 / 送信失敗 {Number(sendApplyResult.failed || 0).toLocaleString()} 件
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function JobSummaryTable({ jobSummaries, onDownloadReport, downloadingJobId, selectedClientId, verifiedSenderProfile, isEnglishBeta = false }) {
    return (
        <div className={cardStyle}>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <h3 className="text-lg font-semibold text-gray-700">{isEnglishBeta ? "Execution Outcome Summary" : "📦 配信JOB別の結果"}</h3>
                <div className="flex flex-col gap-2 sm:items-end">
                    <a
                        href={REPORT_TEMPLATE_URL}
                        download={isEnglishBeta ? "execution-outcome-report-template.xlsx" : "送信結果_簡易レポートテンプレート.xlsx"}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                    >
                        <FileSpreadsheet size={16} />
                        {isEnglishBeta ? "Download report template" : "報告用テンプレートDL"}
                    </a>
                    <span className="text-xs text-gray-500">{isEnglishBeta ? `Latest ${jobSummaries.length} jobs` : `直近 ${jobSummaries.length} 件`}</span>
                </div>
            </div>
            {jobSummaries.length === 0 ? (
                <p className="text-sm text-gray-500">{isEnglishBeta ? "No execution outcomes are available yet." : "まだ表示できる配信JOB結果はありません。"}</p>
            ) : (
                <div className="space-y-4">
                    {jobSummaries.map((job) => {
                        const successLikeCount = Number(job.success_like_count ?? ((Number(job.success_count || 0) + Number(job.needs_review_count || 0))));
                        const failedCount = Number(job.failed_count ?? job.fail_count ?? 0);
                        const excludedCount = Number(job.excluded_count ?? (
                            Number(job.dead_url_count || 0)
                            + Number(job.no_form_found_count || 0)
                            + Number(job.wrong_form_type_count || 0)
                            + Number(job.other_status_count || 0)
                        ));
                        const effectiveDeliveryCount = Number(job.effective_delivery_count || (successLikeCount + failedCount));
                        const processedCount = successLikeCount + failedCount + excludedCount;
                        const clickCompanyCount = Number(job.click_company_count || 0);
                        const ctrBaseCount = successLikeCount;
                        const ctr = ctrBaseCount > 0 ? (clickCompanyCount / ctrBaseCount) * 100 : null;
                        const successRate = effectiveDeliveryCount > 0 ? (successLikeCount / effectiveDeliveryCount) * 100 : null;
                        const isFailed = String(job.job_status || "").toLowerCase() === "failed";
                        const reportConfidence = String(job.report_confidence || "").toLowerCase();
                        const isExactReport = reportConfidence === "exact";
                        const hasDetailRows = Number(job.exact_report_row_count || 0) > 0;
                        const reportBadgeLabel = isEnglishBeta
                            ? (isExactReport ? "Exact" : (hasDetailRows ? "Estimated rows" : "Estimated"))
                            : (isExactReport ? "確定" : (hasDetailRows ? "推定明細" : "推定"));
                        const isDownloading = String(downloadingJobId || "") === String(job.background_job_id);

                        return (
                            <div key={job.background_job_id} className="border rounded-lg p-4 bg-gray-50">
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-bold text-gray-800">JOB {job.background_job_id}</span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isFailed ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                                                {isFailed ? (isEnglishBeta ? "Stopped / Failed" : "停止/失敗") : (isEnglishBeta ? "Completed" : "完了")}
                                            </span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isExactReport ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                                                {reportBadgeLabel}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-700 font-medium">
                                            {job.template_title || (isEnglishBeta ? "Untitled template" : "テンプレ名なし")}
                                        </p>
                                        <div className="mt-2 text-xs text-gray-500 space-y-1">
                                            <p>{isEnglishBeta ? "Execution started" : "配信開始"}: {formatDateTimeLabel(job.job_created_at, isEnglishBeta)}</p>
                                            <p>{isFailed ? (isEnglishBeta ? "Execution stopped" : "配信停止") : (isEnglishBeta ? "Execution completed" : "配信終了")}: {formatDateTimeLabel(job.job_updated_at, isEnglishBeta)}</p>
                                        </div>
                                    </div>
                                    <div className="text-left md:text-right">
                                        <button
                                            type="button"
                                            onClick={() => onDownloadReport(job)}
                                            disabled={isDownloading}
                                            className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <FileSpreadsheet size={16} />
                                            {isDownloading ? (isEnglishBeta ? "Generating..." : "生成中...") : (isEnglishBeta ? "Download Excel" : "Excel DL")}
                                        </button>
                                        <div className="mt-3">
                                            <p className="text-xs text-gray-500">{isEnglishBeta ? "Submitted rate" : "成功率"}</p>
                                            <p className="text-2xl font-bold text-blue-700">
                                                {successRate === null ? "-" : `${successRate.toFixed(1)}%`}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
                                    <div className="bg-white rounded-md border p-3">
                                        <p className="text-[11px] text-gray-500 mb-1">{isEnglishBeta ? "Processed" : "処理件数"}</p>
                                        <p className="text-lg font-bold text-gray-800">{processedCount.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-white rounded-md border p-3">
                                        <p className="text-[11px] text-gray-500 mb-1">{isEnglishBeta ? "Submitted" : "成功"}</p>
                                        <p className="text-lg font-bold text-emerald-700">{successLikeCount.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-white rounded-md border p-3">
                                        <p className="text-[11px] text-gray-500 mb-1">{isEnglishBeta ? "Failed" : "失敗"}</p>
                                        <p className="text-lg font-bold text-red-700">{failedCount.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-white rounded-md border p-3">
                                        <p className="text-[11px] text-gray-500 mb-1">{isEnglishBeta ? "Not eligible" : "対象外"}</p>
                                        <p className="text-lg font-bold text-gray-800">{excludedCount.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-white rounded-md border p-3">
                                        <p className="text-[11px] text-gray-500 mb-1">{isEnglishBeta ? "Submitted / engaged" : "成功 / 反応"}</p>
                                        <p className="text-sm font-bold text-gray-800">{ctrBaseCount.toLocaleString()} / {clickCompanyCount.toLocaleString()}</p>
                                        <p className="text-[11px] text-gray-500 mt-1">CTR {ctr === null ? "-" : `${ctr.toFixed(2)}%`}</p>
                                    </div>
                                </div>
                                <p className="mt-3 text-xs text-gray-500">{getCtrComment(ctr, isEnglishBeta)}</p>
                                {!isEnglishBeta && (
                                    <MailFallbackJobPanel
                                        jobId={job.background_job_id}
                                        clientId={selectedClientId}
                                        verifiedSenderProfile={verifiedSenderProfile}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// --- メインマイページコンポーネント ---
function MyPage() {
    const { user } = useOutletContext();
    const navigate = useNavigate();
    const isEnglishBeta = isEnglishOperatorMode();

    const dedupeCompanyLogs = useCallback((logs = []) => {
        const seen = new Set();
        return logs.filter((log) => {
            const normalizedCompany = String(log.company || "").trim().toLowerCase();
            const key = `${log.list_type || ""}-${log.sales_list_id || ""}-${normalizedCompany}`;
            if (!normalizedCompany || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, []);

    // ステート定義
    const [activeTab, setActiveTab] = useState(() => localStorage.getItem("sb_mypage_tab") || 'outbound');
    const [metaStats, setMetaStats] = useState(DEFAULT_META_STATS);
    const [planInfo, setPlanInfo] = useState(DEFAULT_PLAN_INFO);
    const [clickLogs, setClickLogs] = useState([]);
    const [hotLeads, setHotLeads] = useState([]);
    const [jobSummaries, setJobSummaries] = useState([]);
    const [downloadingJobId, setDownloadingJobId] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [usageLimits, setUsageLimits] = useState(DEFAULT_USAGE_LIMITS);
    const [issuerProfiles, setIssuerProfiles] = useState([]);
    
    const [clientFilter, setClientFilter] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    
    // モーダル管理用
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [showHotLeadPopup, setShowHotLeadPopup] = useState(false);
    const [hotLeadCount, setHotLeadCount] = useState(0);

    useEffect(() => {
        if (isEnglishBeta && activeTab !== 'outbound') {
            setActiveTab('outbound');
            localStorage.setItem("sb_mypage_tab", "outbound");
        }
    }, [activeTab, isEnglishBeta]);

    const isAdmin = useMemo(() => user?.role === "admin" || user?.role === "admin_staff", [user]);
    const selectedClientId = useMemo(() => {
        if (!user) return "";
        return isAdmin ? clientFilter : user.client_id || "";
    }, [user, clientFilter, isAdmin]);

    // データ同期
    const fetchData = useCallback(async () => {
        if (!user || !selectedClientId) { setIsLoading(false); return; }
        const auth = getAuthHeader();
        try {
            const [ann, usage, plan, clicks, hot, meta, summaries, issuerProfilesResponse] = await Promise.all([
                fetchJsonSafely(`${API_URL}/api/announcements?role=${user.role}`, { headers: auth }, { success: false, announcements: [] }, "announcements"),
                fetchJsonSafely(`${API_URL}/api/usage-status?clientId=${selectedClientId}`, { headers: auth }, DEFAULT_USAGE_LIMITS, "usage-status"),
                fetchJsonSafely(`${API_URL}/api/subscription-status?clientId=${selectedClientId}`, { headers: auth }, { success: false, subscription: null }, "subscription-status"),
                fetchJsonSafely(`${API_URL}/api/click-logs?client_id=${selectedClientId}&limit=5`, { headers: auth }, { success: false, logs: [], pagination: { totalItems: 0 } }, "click-logs"),
                fetchJsonSafely(`${API_URL}/api/click-logs?client_id=${selectedClientId}&minClicks=2&limit=5`, { headers: auth }, { success: false, logs: [], pagination: { totalItems: 0 } }, "hot-click-logs"),
                fetchJsonSafely(`${API_URL}/api/marketing/dashboard-stats?client_id=${selectedClientId}`, { headers: auth }, { success: false, stats: DEFAULT_META_STATS }, "marketing-dashboard-stats"),
                fetchJsonSafely(`${API_URL}/api/send-job-summaries?client_id=${selectedClientId}&limit=50`, { headers: auth }, { success: false, summaries: [] }, "send-job-summaries"),
                fetchJsonSafely(`${API_URL}/api/issuer-profiles?client_id=${selectedClientId}`, { headers: auth }, { success: false, profiles: [] }, "issuer-profiles"),
            ]);

            const nextClickLogs = clicks.success ? dedupeCompanyLogs(clicks.logs || []) : [];
            const nextHotLeads = hot.success ? dedupeCompanyLogs(hot.logs || []) : [];
            const nextMetaStats = meta.success ? (meta.stats || DEFAULT_META_STATS) : DEFAULT_META_STATS;

            setAnnouncements(ann.success ? (ann.announcements || []) : []);
            setUsageLimits(usage.success ? usage : DEFAULT_USAGE_LIMITS);
            setPlanInfo(plan.success && plan.subscription ? { plan_name: plan.subscription.plan_name, plan_type: plan.subscription.status } : DEFAULT_PLAN_INFO);
            setClickLogs(nextClickLogs);
            setHotLeads(nextHotLeads);
            setMetaStats(nextMetaStats);
            setJobSummaries(summaries.success ? (summaries.summaries || []) : []);
            setIssuerProfiles(issuerProfilesResponse.success ? (issuerProfilesResponse.profiles || []) : []);

            const count = activeTab === 'outbound'
                ? (hot.pagination?.totalItems || nextHotLeads.length || 0)
                : (nextMetaStats.totalLeads || 0);
            setHotLeadCount(count);

            const isDismissed = sessionStorage.getItem('hotLeadDismissed');
            if (!isEnglishBeta && count > 0 && !isDismissed) {
                setShowHotLeadPopup(true);
            } else {
                setShowHotLeadPopup(false);
            }

        } catch (e) { console.error("Sync Error", e); }
        finally { setIsLoading(false); }
    }, [user, selectedClientId, activeTab, dedupeCompanyLogs, isEnglishBeta]);

    useEffect(() => {
        if (isAdmin) setClientFilter(localStorage.getItem("selectedClientId") || "");
        fetchData();
    }, [user, fetchData, isAdmin]);

    const handleDownloadJobReport = useCallback(async (job) => {
        if (!job?.background_job_id || !selectedClientId) return;

        setDownloadingJobId(job.background_job_id);
        try {
            const params = new URLSearchParams({ client_id: selectedClientId });
            const response = await fetch(`${API_URL}/api/send-job-summaries/${job.background_job_id}/report.xlsx?${params.toString()}`, {
                headers: getAuthHeader(),
            });

            if (!response.ok) {
                let message = "JOBレポートExcelの生成に失敗しました。";
                try {
                    const json = await response.json();
                    message = json?.error || message;
                } catch (parseError) {
                    // keep default message
                }
                throw new Error(message);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const disposition = response.headers.get("content-disposition") || "";
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
            const filename = filenameMatch?.[1] || `send-job-${job.background_job_id}-report.xlsx`;
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("[MyPage] JOB report download failed:", error);
            alert(error.message || "JOBレポートExcelの生成に失敗しました。");
        } finally {
            setDownloadingJobId(null);
        }
    }, [selectedClientId]);

    // ホワイトアウト防止
    const emailCurrent = Number(usageLimits?.limits?.email?.current ?? 0);
    const emailLimit = usageLimits?.limits?.email?.limit == null ? null : Number(usageLimits.limits.email.limit);
    const emailLimitText = emailLimit == null ? "∞" : emailLimit.toLocaleString();
    const emailPct = emailLimit && emailLimit > 0 ? Math.min((emailCurrent / emailLimit) * 100, 100) : 0;
    const verifiedSenderProfile = useMemo(
        () => issuerProfiles.find((profile) =>
            profile?.sender_email && ['Verified', 'Success'].includes(String(profile?.ses_verification_status || ''))
        ) || null,
        [issuerProfiles]
    );

    const handleNavigate = () => {
        sessionStorage.setItem('hotLeadDismissed', 'true');
        setShowHotLeadPopup(false);
        if (isEnglishBeta) {
            navigate('/click-logs');
            return;
        }
        if (activeTab === 'outbound') {
            navigate('/deals', { 
                state: { 
                    autoFilter: { 
                        hasClick: 'yes', 
                        status: '未設定',
                        listTypeFilter: "", // 🚀 「すべて」を指定してマスター/マイリスト両方を探す
                        send_status: "all_sent" // 🚀 フォーム送信済みの中から探す
                    } 
                } 
            });
        } else {
            navigate('/marketing/leads');
        }
    };

    if (isLoading) return <div className="p-20 text-center font-bold text-slate-400">読み込み中...</div>;

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-full space-y-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">{isEnglishBeta ? "Dashboard" : "ダッシュボード"}</h1>
                        <p className="text-xs font-bold text-gray-500 mt-1">{isEnglishBeta ? "Plan" : "契約プラン"}: <span className="text-blue-600 font-bold">{isEnglishBeta ? getEnglishPlanName(planInfo.plan_name) : planInfo.plan_name}</span></p>
                    </div>
                    <div className="flex bg-gray-200 p-1 rounded-lg shadow-inner">
                        <button onClick={() => {setActiveTab('outbound'); localStorage.setItem("sb_mypage_tab", "outbound")}} className={`px-6 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'outbound' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}>{isEnglishBeta ? "Outreach Execution" : "フォームアプローチ"}</button>
                        {!isEnglishBeta && <button onClick={() => {setActiveTab('inbound'); localStorage.setItem("sb_mypage_tab", "inbound")}} className={`px-6 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'inbound' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}>広告・リード計測</button>}
                    </div>
                </div>

                {isEnglishBeta && (
                    <div className="mb-6 rounded-2xl border border-cyan-100 bg-cyan-50 p-5">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h2 className="text-lg font-black text-slate-900">English Operator Beta</h2>
                                <p className="mt-1 text-sm leading-6 text-slate-700">
                                    Start with Company Profile & Suppression, create a Japanese outreach template, then use Start Outreach to queue governed execution. Timestamps remain JST.
                                </p>
                            </div>
                            <a href={SALESBREAKER_ENGLISH_GUIDE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">
                                Open English Guide
                            </a>
                        </div>
                    </div>
                )}

                {/* 1. お知らせ (共通・最上部) */}
                {!isEnglishBeta && announcements.length > 0 && (
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100 mb-6 flex items-center gap-3">
                        <span className="text-blue-600 text-lg">📢</span>
                        <p className="text-sm font-semibold text-gray-800 truncate flex-grow">{announcements[0].title}</p>
                        <button onClick={() => setSelectedAnnouncement(announcements[0])} className="text-xs font-bold text-blue-600 underline">詳細を表示</button>
                    </div>
                )}

                {/* 2. 配信モニター */}
                <ActiveJobMonitor isEnglishBeta={isEnglishBeta} />

                {/* --- タブ別表示切り替え --- */}
                {activeTab === 'outbound' ? (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <ClientSelectorBar isAdmin={isAdmin} clientFilter={clientFilter} setClientFilter={setClientFilter} />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <HotLeadCard hotLeads={hotLeads} onNavigateToDeals={handleNavigate} isEnglishBeta={isEnglishBeta} />
                            <div className={cardStyle}>
                                <h3 className={cardTitleStyle}>{isEnglishBeta ? "Recent Engagement Events" : "🕵️ 最新のクリックログ"}</h3>
                                {clickLogs.length > 0 ? (
                                    <ul className="space-y-2">
                                        {clickLogs.slice(0, 5).map((log, i) => (
                                            <li key={i} className="text-sm text-gray-700 truncate p-2 hover:bg-gray-50 rounded-md">
                                                {isEnglishBeta ? `${log.company} engaged with the tracked URL` : `${log.company} がURLをクリックしました`}
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p className="text-sm text-gray-400 py-4 text-center">{isEnglishBeta ? "No engagement events yet." : "ログはありません。"}</p>}
                            </div>
                        </div>

                        <JobSummaryTable
                            jobSummaries={jobSummaries}
                            onDownloadReport={handleDownloadJobReport}
                            downloadingJobId={downloadingJobId}
                            selectedClientId={selectedClientId}
                            verifiedSenderProfile={verifiedSenderProfile}
                            isEnglishBeta={isEnglishBeta}
                        />
                    </div>
                ) : (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-slate-900 text-white p-8 rounded-lg shadow-lg relative overflow-hidden border border-slate-800">
                                <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-8 font-mono italic">Meta連携（リアルタイム計測）</h3>
                                <div className="flex justify-between items-end">
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">計測できたCV</p>
                                        <p className="text-6xl font-bold leading-none">{metaStats.conversions || 0}</p>
                                        <p className="text-[10px] text-emerald-400 font-bold mt-2 italic">収益: ¥{(metaStats.revenue || 0).toLocaleString()}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-slate-500 mb-1">獲得リード数</p>
                                        <p className="text-2xl font-bold">{metaStats.totalLeads || 0}</p>
                                        <button onClick={() => navigate('/marketing/leads')} className="mt-4 px-6 py-2 bg-blue-600 rounded-md text-xs font-bold hover:bg-blue-500 transition-all shadow-xl">リード一覧を表示</button>
                                    </div>
                                </div>
                            </div>
                            <div className={cardStyle}>
                                <h3 className={cardTitleStyle}>📧 メール配信機能</h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between text-xs font-bold">
                                        <span className="text-gray-500">今月の配信状況</span>
                                        <span className="text-blue-600 font-bold">{emailCurrent.toLocaleString()} / {emailLimitText}</span>
                                    </div>
                                    <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden shadow-inner">
                                        <div className="bg-blue-600 h-full transition-all duration-1000" style={{ width: `${emailPct}%` }}></div>
                                    </div>
                                    <button onClick={() => navigate('/marketing/email')} className="w-full py-3 bg-gray-900 text-white font-bold rounded-lg text-sm hover:bg-gray-800 transition-all shadow-md">新規キャンペーン作成</button>
                                </div>
                            </div>
                        </div>
                        <div className={cardStyle}>
                            <h3 className={cardTitleStyle}>🌐 直近のWeb計測状況</h3>
                            <div className="p-5 bg-green-50 border border-green-100 rounded-md flex items-center gap-4">
                                <span className="flex h-3 w-3 rounded-full bg-green-500 animate-pulse"></span>
                                <div><p className="text-sm font-bold text-green-700">計測タグ正常動作中</p><p className="text-xs text-green-600 mt-0.5 italic">LPおよびWebサイトからの流入信号をリアルタイムで待機しています...</p></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* モーダル類 */}
            <AnnouncementModal isOpen={!!selectedAnnouncement} onClose={() => setSelectedAnnouncement(null)} announcement={selectedAnnouncement} />
            <HotLeadPopup 
                isOpen={showHotLeadPopup} 
                onClose={() => {
                    // 🚀 修正：閉じるボタンが押されたらフラグを立てる
                    setShowHotLeadPopup(false);
                    sessionStorage.setItem('hotLeadDismissed', 'true');
                }} 
                leadCount={hotLeadCount} 
                type={activeTab} 
                onNavigate={handleNavigate} 
            />
        </div>
    );
}

export default MyPage;
